import { useRef, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Container } from '@/components/layout/Container';
import { Section } from '@/components/layout/Section';
import { Eyebrow } from '@/components/domain/Eyebrow';
import { Button } from '@/components/primitives/Button';
import { useFadeUpOnScroll } from '@/hooks/useFadeUpOnScroll';
import styles from './DecisionDetailSection.module.css';

export const DecisionDetailSection: FC = () => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  useFadeUpOnScroll(ref, { selector: '[data-fade]', stagger: 0.06 });

  const fields = t('home.decisionDetail.fields', { returnObjects: true }) as Array<{
    label: string;
    value: string;
  }>;
  const actions = t('home.decisionDetail.actions', { returnObjects: true }) as string[];

  return (
    <Section tone="muted" id="decision-detail">
      <Container size="default">
        <div ref={ref}>
          <header className={styles.header}>
            <div data-fade>
              <Eyebrow>{t('home.decisionDetail.kicker')}</Eyebrow>
            </div>
            <h2 className={styles.title} data-fade>
              {t('home.decisionDetail.title')}
            </h2>
          </header>

          <div className={styles.card} data-fade>
            <dl className={styles.fields}>
              {fields.map((f) => (
                <div key={f.label} className={styles.field}>
                  <dt className={styles.fieldLabel}>{f.label}</dt>
                  <dd className={styles.fieldValue}>
                    {f.value}
                    {f.label === 'Anchor Hash' || f.label === '锚定哈希' ? (
                      <span className={styles.copyHint} aria-hidden="true">
                        📋
                      </span>
                    ) : null}
                  </dd>
                </div>
              ))}
            </dl>
            <div className={styles.actions}>
              {actions.map((label, idx) => (
                <Button key={label} variant={idx === 0 ? 'secondary' : 'primary'} size="md">
                  {label} {idx === 1 ? '→' : ''}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </Container>
    </Section>
  );
};

DecisionDetailSection.displayName = 'DecisionDetailSection';
